// shared/src/resolvers/fred-series-label.ts
// Translation-in-code (Part A of docs/macro-grounding-validator-2026-06-11-brief-revision.md):
// canonical human labels for the 15 FRED MACRO_SNAPSHOT_SERIES IDs so the LLM
// never sees a bare series ID. Root cause RC1 of the 2026-06-10 incident: the
// macro stages received `CPIAUCSL` (headline) vs `CPILFESL` (core) as unlabeled
// raw IDs and swapped them (took the headline 4.2% actual, called it a core
// "consensus"). [[feedback_translation_in_code_not_llm]].
//
// Keep IN LOCKSTEP with workers/refresh-fetcher/src/lib/fred-tools.ts
// MACRO_SNAPSHOT_SERIES — every ID in that batch MUST have an entry here
// (asserted by fred-series-label.test.ts).

/**
 * How the value should be framed for grounding/derivation:
 *   · 'yoy'   — a price index/level; the canonical figure is the YoY %
 *               change (Part B derives `yoy_pct`; the validator grounds against
 *               it). The raw value alone (an index like 326.1) is meaningless
 *               to the reader.
 *   · 'level' — already a rate/percent/count meaningful as-is (FEDFUNDS, DGS10,
 *               UNRATE…); report the level, not a YoY of a rate.
 */
export type FredSeriesTransform = 'yoy' | 'level';

export interface FredSeriesLabel {
  /** Canonical human label shown to the LLM in place of the bare ID. */
  readonly label:     string;
  /** Unit of the RAW series value (so 'index' signals "derive YoY to interpret"). */
  readonly unit:      string;
  /** Coarse family for grouping / validator routing. */
  readonly family:    string;
  /** Canonical framing — see FredSeriesTransform. */
  readonly transform: FredSeriesTransform;
}

/**
 * FRED series ID → canonical label. Frozen; the single source of truth for how
 * macro series are named to the LLM and on the dashboard (ZoneMacro can reuse).
 */
export const FRED_SERIES_LABELS: Readonly<Record<string, FredSeriesLabel>> = Object.freeze({
  FEDFUNDS:     { label: 'Federal Funds Rate',                 unit: 'percent', family: 'rates',      transform: 'level' },
  DGS10:        { label: '10-Year Treasury Yield',             unit: 'percent', family: 'rates',      transform: 'level' },
  DGS2:         { label: '2-Year Treasury Yield',              unit: 'percent', family: 'rates',      transform: 'level' },
  T10Y2Y:       { label: '10Y–2Y Treasury Spread',            unit: 'percent', family: 'rates',      transform: 'level' },
  CPIAUCSL:     { label: 'CPI — Headline (all items)',        unit: 'index',   family: 'inflation',  transform: 'yoy'   },
  CPILFESL:     { label: 'CPI — Core (ex food & energy)',     unit: 'index',   family: 'inflation',  transform: 'yoy'   },
  UNRATE:       { label: 'Unemployment Rate',                  unit: 'percent', family: 'labor',      transform: 'level' },
  GDP:          { label: 'Gross Domestic Product (nominal)',   unit: 'usd_bn',  family: 'growth',     transform: 'level' },
  UMCSENT:      { label: 'U. Michigan Consumer Sentiment',     unit: 'index',   family: 'sentiment',  transform: 'level' },
  WALCL:        { label: 'Fed Balance Sheet (total assets)',   unit: 'usd_mn',  family: 'liquidity',  transform: 'level' },
  T10YIEM:      { label: '10-Year Breakeven Inflation',        unit: 'percent', family: 'inflation',  transform: 'level' },
  BAMLH0A0HYM2: { label: 'High-Yield Credit Spread (OAS)',     unit: 'percent', family: 'credit',     transform: 'level' },
  ICSA:         { label: 'Initial Jobless Claims',             unit: 'count',   family: 'labor',      transform: 'level' },
  PCEPILFE:     { label: 'Core PCE Price Index',               unit: 'index',   family: 'inflation',  transform: 'yoy'   },
  M2SL:         { label: 'M2 Money Supply',                    unit: 'usd_bn',  family: 'liquidity',  transform: 'level' },
});

/**
 * The 15 curated macro series IDs, in canonical order — the ordered key list of
 * FRED_SERIES_LABELS. Single source of truth for WHICH series the macro Data
 * Oracle resolves (data_type='macro') and the ZoneMacro dashboard lists; mirrors
 * refresh-fetcher's MACRO_SNAPSHOT_SERIES (asserted by fred-series-label.test.ts).
 */
export const FRED_MACRO_SERIES_IDS: ReadonlyArray<string> = Object.freeze(Object.keys(FRED_SERIES_LABELS));

/**
 * Resolve a FRED series ID to its canonical label, or null when the ID is not
 * in the curated snapshot batch (caller falls back to the raw ID rather than
 * inventing a label — fail-loud-ish, never silently mislabel).
 */
export function fredSeriesLabel(seriesId: string): FredSeriesLabel | null {
  return FRED_SERIES_LABELS[seriesId] ?? null;
}

/**
 * Display string for a series ID: "CPI — Core (ex food & energy)" when known,
 * else the bare ID (so an un-curated series is still identifiable, never hidden).
 */
export function fredSeriesDisplay(seriesId: string): string {
  return FRED_SERIES_LABELS[seriesId]?.label ?? seriesId;
}
